import { Button, Collapse, Divider, Flex, Grid, Popup, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import range from 'lodash/range';
import { Fragment, memo } from 'react';
import { t } from 'services/localization';
import { TwoFaActionWizard } from 'shared/components';
import { ConvertCurrencyPopup, FundWalletPopup } from 'shared/components/(wallets)';
import { useItemsGroupedByWallet, useWalletsByLineItems } from 'shared/hooks/(payments)';

import {
    CollapsedWalletLoading,
    LineItemPaymentQuote,
    LineItemPaymentSummary,
    WalletCard,
    WalletTotalAmount,
} from './components';
import { usePayment } from './hooks/usePayment';
import { popupHeaderHeight } from './WalletsPaymentSummaryPopupContent.constants';
import { messages } from './WalletsPaymentSummaryPopupContent.messages';
import { WalletsPaymentSummaryPopupContentProps } from './WalletsPaymentSummaryPopupContent.types';

export const WalletsPaymentSummaryPopupContent = memo<WalletsPaymentSummaryPopupContentProps>((props) => {
    const { request, onSuccessfulPayment } = props;

    const {
        companyId,
        id: requestId,
        displayName,
        details: { items },
    } = request;

    const { groupedByWallet, enrichedLineItems, isLoadingPaymentDetails, isLoadingQuotes } = useItemsGroupedByWallet(
        companyId,
        requestId,
        items
    );
    const { wallets, isLoadingWallets, onRefreshBalances } = useWalletsByLineItems(
        companyId,
        requestId,
        groupedByWallet
    );
    const { twoFaActionWizardProps, onPay } = usePayment(companyId, requestId, enrichedLineItems, onSuccessfulPayment);

    return (
        <>
            <Popup.Header title={messages.title({ requestTitle: displayName })} />

            <Popup.Body height='100%'>
                <Grid gap={40} gridTemplateColumns='auto 204px' height={`calc(100% - ${popupHeaderHeight}px)`}>
                    <Grid overflowY='hidden'>
                        <Grid
                            gap={16}
                            alignContent='start'
                            overflowY='auto'
                            // this is to make scrollbar and the collapses look more beautiful
                            padding='0 4 0 0'
                        >
                            {isLoadingPaymentDetails
                                ? range(0, 3).map((index) => <CollapsedWalletLoading key={index} />)
                                : groupedByWallet.map((groupedByWallet) => (
                                      <Collapse
                                          bordered
                                          collapsed
                                          size='large'
                                          radius='xsmall'
                                          key={groupedByWallet.currency}
                                          name={t('wallet_currency', { currency: groupedByWallet.currency })}
                                          headerContent={
                                              <WalletTotalAmount
                                                  walletCurrency={groupedByWallet.currency}
                                                  totalAmount={intl.formatCurrency(
                                                      groupedByWallet.total,
                                                      groupedByWallet.currency
                                                  )}
                                              />
                                          }
                                      >
                                          <Grid rowGap={12} padding='0 24 16'>
                                              {groupedByWallet.lineItems.map((lineItem, index) => (
                                                  <Fragment key={lineItem.xeroBillInvoiceRequestId}>
                                                      <LineItemPaymentSummary
                                                          billCurrency={lineItem.currency}
                                                          paymentDetails={lineItem.paymentDetails}
                                                          quote={
                                                              <LineItemPaymentQuote {...lineItem.paymentQuoteProps} />
                                                          }
                                                      />

                                                      {index < groupedByWallet.lineItems.length - 1 && <Divider />}
                                                  </Fragment>
                                              ))}
                                          </Grid>
                                      </Collapse>
                                  ))}
                        </Grid>
                    </Grid>

                    <Flex justifyContent='space-between' direction='column' spacing='24'>
                        <Grid gap={16}>
                            <Grid justifyContent='end'>
                                <Button variant='text' size='xsmall' color='blue100' onClick={onRefreshBalances}>
                                    {t('refresh_balances')}
                                </Button>
                            </Grid>

                            <Grid gap={12}>
                                {isLoadingWallets
                                    ? range(0, 3).map((_, index, arr) => (
                                          <Fragment key={index}>
                                              <WalletCard.Loading />

                                              {arr.length - 1 !== index && <Divider />}
                                          </Fragment>
                                      ))
                                    : wallets?.map((currency, index) => (
                                          <Fragment key={currency.id}>
                                              <WalletCard companyId={companyId} {...currency} />

                                              {wallets.length - 1 !== index && <Divider />}
                                          </Fragment>
                                      ))}
                            </Grid>

                            <Button
                                variant='filled'
                                color='cobalt80'
                                disabled={isLoadingPaymentDetails || isLoadingQuotes}
                                onClick={onPay}
                            >
                                {t('pay_now')}
                            </Button>
                        </Grid>

                        <Text font='body' fontSize='xsmall' color='midnight70'>
                            {t('payment_services_for_approvalmax_are_provided_by')}
                        </Text>
                    </Flex>
                </Grid>

                <TwoFaActionWizard {...twoFaActionWizardProps} />

                <FundWalletPopup />

                <ConvertCurrencyPopup />
            </Popup.Body>
        </>
    );
});

WalletsPaymentSummaryPopupContent.displayName = 'WalletsPaymentSummaryPopupContent';
